import axios from "axios";
import { videotokenUrl } from "../../config/stream";

/**
 * Fetches a chat token for Stream Chat authentication
 * @param {string} userId - The user ID
 * @returns {Promise<string>} The authentication token
 */
export const fetchChatToken = async (userId) => {
  try {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const response = await axios.post(videotokenUrl, {
      userId,
      userName: user.name || "User",
      userImage: user.photoUrl || "",
    });
    return response.data.token;
  } catch (error) {
    console.error("Failed to fetch chat token:", error);
    throw new Error("Could not fetch authentication token");
  }
};

/**
 * Fetches a video token for Stream Video authentication
 * @param {string} userId - The user ID
 * @returns {Promise<string>} The video authentication token
 */
export const fetchVideoToken = async (userId) => {
  try {
    const response = await axios.post(videotokenUrl, {
      userId,
    });
    return response.data.token;
  } catch (error) {
    console.error("Failed to fetch video token:", error);
    throw new Error("Could not fetch video authentication token");
  }
};

/**
 * Checks if a user can create breakout rooms for a class
 * @param {string} classId - The class ID
 * @returns {boolean} True if user can create breakout rooms
 */
export const canCreateBreakoutRooms = (classId) => {
  try {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (!user) return false;

    const { userType, adminOfClasses = [], tutorOfClasses = [] } = user;

    if (userType === "student") {
      return adminOfClasses.includes(classId);
    } else if (userType === "tutor") {
      return tutorOfClasses.includes(classId);
    }
    return false;
  } catch (error) {
    console.error("Error checking breakout room permissions:", error);
    return false;
  }
};

/**
 * Gets the current day abbreviation
 * @returns {string} Day abbreviation (Sun, Mon, Tue, etc.)
 */
export const getCurrentDayAbbreviation = () => {
  const dayAbbreviations = [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
  ];
  return dayAbbreviations[new Date().getDay()];
};

/**
 * Generates a channel ID for a class
 * @param {string} classId - The class ID
 * @param {string} activeRoomId - The active room ID (for breakout rooms)
 * @returns {string} The channel ID
 */
export const generateChannelId = (classId, activeRoomId = null) => {
  const currentDay = getCurrentDayAbbreviation();
  const isBreakoutRoom = activeRoomId && activeRoomId !== classId;
  return isBreakoutRoom
    ? `${currentDay}${classId}${activeRoomId}`
    : `${currentDay}${classId}`;
};

/**
 * Gets user data from session storage
 * @returns {Object} User object
 */
export const getUserFromSession = () => {
  return JSON.parse(sessionStorage.getItem("user") || "{}");
};

